import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterClassListQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import Nav from '@/modules/issuer-dashboard/components/dashboard/Nav.tsx';
import { NavInfoTable } from '@/modules/issuer-dashboard/components/modals/IssuerNavInfoModal.tsx';
import { IssuerAddNavModal } from '@/modules/issuer-dashboard/components/nav-managment/IssuerAddNavModal.tsx';
import { SelectDate } from '@/modules/issuer-dashboard/components/transactions/SelectDate.tsx';
import { SelectRegisterClass } from '@/modules/issuer-dashboard/components/transactions/SelectRegisterClass.tsx';
import { useIssuerNavListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { Button, ModalProviderContext, SelectPaginateOption } from 'ui';
import { useDeferredSectionCount } from 'ui/hooks';

export const NavHistoryTab = () => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [registerClass, setRegisterClass] = useState<SelectPaginateOption<number> | undefined>(
    undefined,
  );
  const [createdDates, setCreatedDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [activeDates, setActiveDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const visibleSectionCount = useDeferredSectionCount(3);

  const { data, isFetching } = useRegisterClassListQuery({
    page: 1,
    page_size: 1,
    register_id: +registerId,
  });

  useEffect(() => {
    const firstClass = data?.data?.[0];
    if (!registerClass && firstClass?.id) {
      setRegisterClass(
        firstClass?.id ? { value: firstClass?.id, label: firstClass?.class_name } : undefined,
      );
    }
  }, [data?.data, registerClass]);

  const handleChangeCreatedDates = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setCreatedDates(dates);
    },
    [setCreatedDates],
  );

  const handleChangeActiveDates = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setActiveDates(dates);
    },
    [setActiveDates],
  );

  return (
    <div className="flex flex-col w-full gap-4">
      {visibleSectionCount >= 1 && (
        <div className="px-3 sm:px-6">
          <Nav showFullName={false} />
        </div>
      )}
      {visibleSectionCount >= 2 && (
        <div className="flex xl:flex-row flex-col xl:items-center items-start xl:justify-between justify-start px-3 sm:px-6 gap-4 pt-2">
          <SelectRegisterClass
            registerId={registerId}
            registerClass={registerClass}
            setRegisterClass={setRegisterClass}
            isFetching={isFetching}
          />
          <div className="flex lg:flex-row flex-col xl:items-center items-start gap-4">
            <div>
              <SelectDate
                dates={createdDates}
                handleChangeDates={handleChangeCreatedDates}
                label="Created At"
              />
            </div>
            <div>
              <SelectDate
                dates={activeDates}
                handleChangeDates={handleChangeActiveDates}
                label="Active From"
              />
            </div>
            <div>
              <ButtonUpdateNav registerClassId={registerClass?.value} />
            </div>
          </div>
        </div>
      )}
      {visibleSectionCount >= 3 && (
        <NavInfoTable
          withBorder={false}
          register_class_id={registerClass?.value}
          createdDates={createdDates}
          activeDates={activeDates}
          limit={15}
          blockIdInfinite="scrollable"
        />
      )}
    </div>
  );
};

interface ButtonUpdateNavProps {
  registerClassId?: number;
  className?: string;
  variant?: 'primary' | 'secondary' | 'plain';
}

export const ButtonUpdateNav = ({ registerClassId, className, variant }: ButtonUpdateNavProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { openModal } = useContext(ModalProviderContext);

  const { data: navList } = useIssuerNavListQuery(
    {
      page: 1,
      page_size: 20,
      register_class_id: registerClassId ?? 0,
    },
    !registerClassId,
  );

  const activeNav = useMemo(
    () => navList?.pages?.flatMap(page => page.data).find(item => item.is_active === true),
    [navList?.pages],
  );

  const handleAdd = () => {
    openModal(IssuerAddNavModal, {
      register_class_id: registerClassId ?? 0,
      activeDate: new Date(activeNav?.changes_date ?? new Date()),
      defaultPricePerShare: activeNav?.price_per_share,
    });
  };

  return (
    <Button className={classNames('w-[120px]', className)} variant={variant} onClick={handleAdd}>
      Update {getRegisterTypeStrings('NAV')}
    </Button>
  );
};
